import { z } from "zod"

/**
 * Schema for creating/editing maintenance checks.
 * Validation rules match backend constraints.
 */
export const maintenanceSchema = z.object({
  // Required fields
  checkedAt: z.date({ message: "Check date/time is required" }),

  waterLevel: z
    .number({ message: "Water level is required" })
    .min(0, "Water level must be at least 0"),

  // Equipment status (required booleans with defaults)
  waterFlowCheck: z.boolean(),
  paddleWheelFunctioning: z.boolean(),

  // Environmental observations (required booleans with defaults)
  stressedFishAtInlets: z.boolean(),
  algalBloom: z.boolean(),
  presenceOfBirds: z.boolean(),

  // Optional fields
  procedureFollowed: z
    .string()
    .max(500, "Procedure must be less than 500 characters")
    .optional()
    .nullable(),

  dischargePh: z
    .number()
    .min(0, "Must be at least 0")
    .max(14, "Must be at most 14")
    .optional()
    .nullable(),

  dischargeDo: z
    .number()
    .min(0, "Must be at least 0")
    .max(100, "Must be at most 100 mg/L")
    .optional()
    .nullable(),

  dischargeFlowRate: z
    .number()
    .min(0, "Must be at least 0")
    .optional()
    .nullable(),

  notes: z.string().max(2000, "Notes must be less than 2000 characters").optional().nullable(),

  imageIds: z.array(z.string()).max(5, "Maximum 5 images allowed").optional(),
})

export type MaintenanceFormData = z.infer<typeof maintenanceSchema>

/**
 * Convert form data to API request format.
 */
export function toApiRequest(data: MaintenanceFormData) {
  return {
    checkedAt: data.checkedAt.toISOString(),
    waterLevel: data.waterLevel,
    waterFlowCheck: data.waterFlowCheck,
    paddleWheelFunctioning: data.paddleWheelFunctioning,
    stressedFishAtInlets: data.stressedFishAtInlets,
    algalBloom: data.algalBloom,
    presenceOfBirds: data.presenceOfBirds,
    ...(data.procedureFollowed && { procedureFollowed: data.procedureFollowed }),
    ...(data.dischargePh != null && { dischargePh: data.dischargePh }),
    ...(data.dischargeDo != null && { dischargeDo: data.dischargeDo }),
    ...(data.dischargeFlowRate != null && { dischargeFlowRate: data.dischargeFlowRate }),
    ...(data.notes && { notes: data.notes }),
    ...(data.imageIds && data.imageIds.length > 0 && { imageIds: data.imageIds }),
  }
}
